import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Compass, Loader2, Sparkles, MapPin, Users, ExternalLink, TrendingUp,
  ArrowRight, Zap, Building2, Calendar, Award, RefreshCcw, PlusCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const TYPE_ICON = {
  chamber: Building2, event: Calendar, conference: Calendar, salon: Calendar,
  workshop: Calendar, incubator: Zap, accelerator: Zap, gov_program: Award,
  subvention: Award, partnership: Users, partnership_call: Users,
  conference_slot: Award, kiosk_slot: Award, real_estate_project: MapPin,
  organisation: Users, business_new: Building2, commerce_new: Building2,
  territory: MapPin,
};

const TYPE_LABEL = {
  chamber: "Chambre", event: "Événement", conference: "Conférence",
  salon: "Salon", workshop: "Atelier", incubator: "Incubateur",
  accelerator: "Accélérateur", gov_program: "Programme", subvention: "Subvention",
  partnership: "Partenariat", partnership_call: "Appel à partenariat",
  conference_slot: "Conférencier", kiosk_slot: "Kiosque",
  real_estate_project: "Immobilier", organisation: "Organisme",
  business_new: "Entreprise", commerce_new: "Commerce",
  territory: "Territoire",
};

function ScoreBadge({ score }) {
  const s = Number(score || 0);
  const tone = s >= 90 ? "bg-emerald-100 text-emerald-800 border-emerald-300"
              : s >= 75 ? "bg-amber-100 text-amber-800 border-amber-300"
              : "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${tone}`}
          data-testid="radar-opp-score">
      <TrendingUp size={12} /> {s}
    </span>
  );
}

function TypeBadge({ type }) {
  const Icon = TYPE_ICON[type] || Compass;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wide bg-navy-50 text-navy-700 border border-navy-200"
          style={{background:"#0B2545", color:"#F5F0E6"}}>
      <Icon size={11} /> {TYPE_LABEL[type] || type}
    </span>
  );
}

function OpportunityCard({ opp, onOpen }) {
  const t = opp.territory || {};
  const src = opp.source || {};
  return (
    <button
      type="button"
      onClick={() => onOpen(opp)}
      className="text-left w-full bg-white border border-slate-200 rounded-xl p-4 hover:border-gold-400 hover:shadow-md transition-all"
      data-testid={`radar-opp-card-${opp.opportunity_id}`}
      style={{borderColor:"#E5E7EB"}}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <TypeBadge type={opp.opportunity_type} />
        <ScoreBadge score={opp.priority_score} />
      </div>
      <h3 className="font-serif text-lg font-semibold text-slate-900 leading-tight mb-1"
          style={{color:"#0B2545"}}>{opp.title_fr}</h3>
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{opp.description_fr}</p>
      <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
        {(t.city || t.province) && (
          <span className="inline-flex items-center gap-1">
            <MapPin size={11} /> {[t.city, t.province].filter(Boolean).join(", ")}
          </span>
        )}
        {src.name && (
          <span className="inline-flex items-center gap-1">
            · {src.name}
          </span>
        )}
        {opp.ai_ranking?.relevance_score !== undefined && (
          <span className="inline-flex items-center gap-1 text-purple-700">
            <Sparkles size={11} /> IA {opp.ai_ranking.relevance_score}
          </span>
        )}
      </div>
    </button>
  );
}

function OpportunityDrawer({ opp, onClose, onConverted }) {
  const [ai, setAi] = useState(opp?.ai_ranking || null);
  const [ranking, setRanking] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => { setAi(opp?.ai_ranking || null); }, [opp?.opportunity_id]);

  const rank = async () => {
    setRanking(true);
    try {
      const r = await api.post(`/abi/radar/opportunities/${opp.opportunity_id}/rank`);
      setAi(r.data?.ai_ranking || null);
      toast.success("Analyse IA générée.");
    } catch (e) {
      toast.error("Erreur lors de l'analyse IA.");
    } finally { setRanking(false); }
  };

  const convert = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/abi/radar/opportunities/${opp.opportunity_id}/convert-to-lead`);
      if (r.data?.already_linked) {
        toast.info("Déjà convertie en cible commerciale.");
      } else {
        toast.success(`Convertie en cible commerciale (${r.data?.target_id?.slice(0,12)}…)`);
      }
      onConverted?.(r.data);
    } catch (e) {
      toast.error("Erreur lors de la conversion.");
    } finally { setConverting(false); }
  };

  if (!opp) return null;
  const t = opp.territory || {};
  const src = opp.source || {};

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40" onClick={onClose}
         data-testid="radar-opp-drawer">
      <div
        className="w-full max-w-2xl h-full bg-white overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <TypeBadge type={opp.opportunity_type} />
                <ScoreBadge score={opp.priority_score} />
              </div>
              <h2 className="font-serif text-2xl font-bold text-slate-900 leading-tight"
                  style={{color:"#0B2545"}}>{opp.title_fr}</h2>
            </div>
            <button className="text-slate-500 hover:text-slate-900" onClick={onClose}
                    data-testid="radar-opp-drawer-close">✕</button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-slate-700 leading-relaxed">{opp.description_fr}</p>

          {/* The 4 questions — always displayed */}
          <div className="grid grid-cols-1 gap-3">
            {opp.why_relevant_short_fr && (
              <div className="border-l-4 pl-3 py-1" style={{ borderColor: "#C5A880" }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                  Pourquoi cette opportunité est intéressante
                </div>
                <p className="text-sm text-slate-800 mt-0.5">{opp.why_relevant_short_fr}</p>
              </div>
            )}
            {opp.recommended_action_fr && (
              <div className="border-l-4 pl-3 py-1" style={{ borderColor: "#0B2545" }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#0B2545" }}>
                  Action concrète à entreprendre
                </div>
                <p className="text-sm text-slate-800 mt-0.5 font-medium">{opp.recommended_action_fr}</p>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {opp.estimated_time_minutes && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                  ⏱ {opp.estimated_time_minutes} min estimés
                </span>
              )}
              {opp.expected_benefit_kind && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                       style={{ background: "#FBF6EA", color: "#7A5B14" }}>
                  🎯 {opp.expected_benefit_detail_fr || opp.expected_benefit_kind}
                </span>
              )}
            </div>
          </div>

          {/* Score breakdown (5 criteria) */}
          {opp.score_breakdown && (
            <div className="border rounded-xl p-4 bg-slate-50" style={{ borderColor: "#E5E7EB" }}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
                  Pourquoi ce score {opp.score_breakdown.total_100}/100
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                {[
                  ["pertinence_activite",     "Pertinence pour votre activité"],
                  ["proximite_geographique",  "Proximité géographique"],
                  ["accessibilite_contact",   "Accessibilité du contact"],
                  ["potentiel_relation",      "Potentiel de relation"],
                  ["urgence_ou_actualite",    "Urgence / actualité"],
                ].map(([k, l]) => {
                  const v = opp.score_breakdown[k] || 0;
                  return (
                    <div key={k} className="flex items-center gap-3">
                      <div className="flex-1 text-slate-700 text-xs">{l}</div>
                      <div className="flex gap-0.5" data-testid={`radar-score-${k}`}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <span key={s}
                                 className="w-2.5 h-2.5 rounded-full"
                                 style={{ background: s <= v ? "#C5A880" : "#E5E7EB" }} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="uppercase tracking-wide text-[10px] text-slate-500 mb-1">Territoire</div>
              <div className="text-slate-800">{[t.city, t.province, t.country].filter(Boolean).join(" · ")}</div>
            </div>
            <div>
              <div className="uppercase tracking-wide text-[10px] text-slate-500 mb-1">Source</div>
              <div className="text-slate-800">{src.name}</div>
              {src.url && (
                <a href={src.url} target="_blank" rel="noreferrer"
                   className="text-xs text-blue-600 inline-flex items-center gap-1 hover:underline">
                  <ExternalLink size={11} /> Consulter la source
                </a>
              )}
            </div>
            {opp.sectors?.length > 0 && (
              <div>
                <div className="uppercase tracking-wide text-[10px] text-slate-500 mb-1">Secteurs</div>
                <div className="text-slate-800">{opp.sectors.join(", ")}</div>
              </div>
            )}
            {opp.target_professions?.length > 0 && (
              <div>
                <div className="uppercase tracking-wide text-[10px] text-slate-500 mb-1">Profils cibles</div>
                <div className="text-slate-800 text-xs">{opp.target_professions.join(", ")}</div>
              </div>
            )}
          </div>

          {/* AI Analysis */}
          <div className="border rounded-xl p-4"
               style={{background:"#FBF6EA", borderColor:"#C5A880"}}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-semibold"
                   style={{color:"#7A5B14"}}>
                <Sparkles size={16} /> Analyse ALTIORA Intelligence
              </div>
              <button
                type="button"
                onClick={rank}
                disabled={ranking}
                className="text-xs px-3 py-1 rounded-full border hover:bg-white transition disabled:opacity-50"
                style={{borderColor:"#C5A880", color:"#7A5B14"}}
                data-testid="radar-opp-rank-btn"
              >
                {ranking ? (<><Loader2 size={12} className="inline animate-spin mr-1" /> Analyse...</>)
                          : (<><RefreshCcw size={12} className="inline mr-1" /> {ai ? "Recalculer" : "Analyser"}</>)}
              </button>
            </div>
            {ai ? (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Score de pertinence</div>
                  <div className="text-2xl font-serif font-bold" style={{color:"#0B2545"}}>{ai.relevance_score}/100</div>
                </div>
                {ai.why_relevant_fr && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Pourquoi cette opportunité est prioritaire</div>
                    <p className="text-slate-800">{ai.why_relevant_fr}</p>
                  </div>
                )}
                {ai.suggested_action_fr && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Action suggérée</div>
                    <p className="text-slate-800 font-medium">{ai.suggested_action_fr}</p>
                  </div>
                )}
                {ai.risk_flags_fr?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Points de vigilance</div>
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      {ai.risk_flags_fr.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                <div className="text-[10px] text-slate-500">
                  Modèle : {ai.model} · {ai.ranked_at?.slice(0, 16)}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600 italic">
                Aucune analyse IA générée pour cette opportunité. Cliquez sur <b>Analyser</b> pour
                obtenir une explication personnalisée basée sur votre profession et votre territoire.
              </p>
            )}
          </div>

          {/* Convert to lead */}
          <div className="border-t pt-4 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-600">
              {opp.linked_target_id
                ? <>✓ Déjà convertie en cible <code className="text-xs">{opp.linked_target_id}</code></>
                : "Prête à devenir une opportunité commerciale ?"}
            </div>
            <button
              type="button"
              onClick={convert}
              disabled={converting || !!opp.linked_target_id}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white hover:opacity-90 disabled:opacity-50"
              style={{background:"#0B2545"}}
              data-testid="radar-opp-convert-btn"
            >
              {converting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              Convertir en cible commerciale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AbiRadar() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [briefRes, listRes] = await Promise.all([
        api.get("/abi/radar/morning-briefing?top_n=6"),
        api.get(`/abi/radar/opportunities${typeFilter ? `?opportunity_type=${typeFilter}` : ""}`),
      ]);
      setBriefing(briefRes.data);
      setItems(listRes.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Accès réservé aux professionnels ALTIORA.");
      } else {
        toast.error("Impossible de charger le radar.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/abi/radar/seed");
      toast.success(`Catalogue hydraté (+${r.data?.inserted || 0} entrées)`);
      await load();
    } catch (e) {
      toast.error("Seed refusé (réservé à l'admin).");
    } finally { setSeeding(false); }
  };

  const top = briefing?.top || [];
  const types = Array.from(new Set(items.map((i) => i.opportunity_type)));

  return (
    <div data-testid="abi-radar-root">
      <TopBar
        title="ALTIORA Radar"
        subtitle="Découvrez chaque matin les meilleures opportunités économiques de votre région."
      />
      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1400px] mx-auto space-y-8">

        {/* Morning briefing */}
        <div className="rounded-2xl p-6 md:p-8" style={{background:"linear-gradient(135deg,#0B2545 0%,#061630 100%)", color:"#F5F0E6"}}>
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="text-xs uppercase tracking-widest" style={{color:"#C5A880"}}>Briefing du matin</div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border hover:bg-white/10 disabled:opacity-50"
              style={{borderColor:"#C5A880", color:"#C5A880"}}
              data-testid="abi-radar-refresh"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
              Actualiser
            </button>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            Vos meilleures opportunités aujourd'hui
          </h1>
          <p className="text-sm md:text-base opacity-80 max-w-2xl">
            Profession active : <b>{briefing?.user_profession || "—"}</b> · Territoire :{" "}
            <b>{[briefing?.user_territory?.city, briefing?.user_territory?.province].filter(Boolean).join(", ") || "—"}</b>
            {" · "}
            <span className="opacity-70">{briefing?.total_available || 0} opportunités actives dans votre écosystème.</span>
          </p>

          {loading && !briefing && (
            <div className="mt-6 flex items-center gap-2 text-sm">
              <Loader2 size={16} className="animate-spin" /> Chargement…
            </div>
          )}

          {!loading && top.length === 0 && (
            <div className="mt-6 space-y-3">
              <p className="text-sm opacity-80">Aucune opportunité chargée. Hydratez le catalogue pour commencer.</p>
              <button
                type="button"
                onClick={seed}
                disabled={seeding}
                className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
                style={{background:"#C5A880", color:"#0B2545"}}
                data-testid="abi-radar-seed-btn"
              >
                {seeding ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
                Hydrater le catalogue
              </button>
            </div>
          )}

          {top.length > 0 && (
            <ol className="mt-6 space-y-2">
              {top.slice(0, 6).map((opp, i) => (
                <li key={opp.opportunity_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(opp)}
                    className="w-full text-left px-4 py-3 rounded-lg border hover:bg-white/5 transition flex items-center gap-3"
                    style={{borderColor:"rgba(197,168,128,0.3)"}}
                    data-testid={`abi-radar-briefing-item-${i}`}
                  >
                    <span className="font-serif text-2xl font-bold w-8" style={{color:"#C5A880"}}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{opp.title_fr}</div>
                      <div className="text-xs opacity-70 truncate">
                        {TYPE_LABEL[opp.opportunity_type] || opp.opportunity_type}
                        {" · "}
                        {[opp.territory?.city, opp.territory?.province].filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <span className="font-bold text-lg" style={{color:"#C5A880"}}>{opp.priority_score}</span>
                    <ArrowRight size={16} className="opacity-60" />
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setTypeFilter("")}
            className={`text-xs px-3 py-1 rounded-full border ${typeFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
            data-testid="abi-radar-filter-all"
          >
            Tout
          </button>
          {types.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={`text-xs px-3 py-1 rounded-full border ${typeFilter === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              data-testid={`abi-radar-filter-${t}`}
            >
              {TYPE_LABEL[t] || t}
            </button>
          ))}
        </div>

        {/* Full grid */}
        <div>
          <h2 className="font-serif text-xl font-bold text-slate-900 mb-4"
              style={{color:"#0B2545"}}>Toutes les opportunités ({items.length})</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600">
              <Loader2 size={16} className="animate-spin" /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <p className="text-slate-500 italic">Aucune opportunité pour ce filtre.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                 data-testid="abi-radar-grid">
              {items.map((o) => (
                <OpportunityCard key={o.opportunity_id} opp={o} onOpen={setSelected} />
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <OpportunityDrawer opp={selected} onClose={() => setSelected(null)}
                            onConverted={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}
